import { Injectable, inject } from "@angular/core";
import { CanActivate, Router, UrlTree } from "@angular/router";
import { AuthService, UserService } from "@services";
import { Store } from "@store";
import { Observable, combineLatest, map, take, catchError, of } from "rxjs";
import type { IFuturesNewChallengeOptionConfiguration } from "../../futures/classes/FuturesNewChallengeOptionConfiguration";
import { selectFuturesNewChallenge } from "../../store/futures-new-challenge/futures-new-challenge.selector";

const CREATE_NEW_CHALLENGE_PAGE_URL: string = "/futures/new-challenge";
const PAYMENT_PAGE_URL: string = "/futures/new-challenge/payment";

@Injectable({ providedIn: "root" })
export class FuturesOrderDetailsPageGuard implements CanActivate {
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _router: Router = inject(Router);
  private readonly _store: Store = inject(Store);
  private readonly _userService: UserService = inject(UserService);

  public canActivate(): Observable<boolean | UrlTree> {
    return combineLatest([
      this._authService.isAuthenticated(),
      this._store.select(selectFuturesNewChallenge),
      this._userService.isMissingKycData().pipe(
        // fallback if the request fails because you're not yet logged in
        catchError((): Observable<boolean> => of(true)),
      ),
    ]).pipe(
      take(1),
      map(
        ([isAuthenticated, selectedChallenge, isMissingKycData]: [
          boolean,
          IFuturesNewChallengeOptionConfiguration | null,
          boolean,
        ]): boolean | UrlTree => {
          if (!selectedChallenge) {
            return this._router.parseUrl(CREATE_NEW_CHALLENGE_PAGE_URL);
          }

          if (isAuthenticated && !isMissingKycData) {
            return this._router.parseUrl(PAYMENT_PAGE_URL);
          }

          return true;
        },
      ),
    );
  }
}
